import {
  ALL_CLIENTS,
  findClient,
  filterClients,
  computeTypeStats,
  buildAccountsForClient,
  buildConventionsForClient,
  buildNotesForClient,
  buildOpenAccountsSeries,
  buildKpiSeries,
  buildClientUsersCount,
} from '../fixtures/clients';

const paginate = (items, params) => {
  const page = Number(params.page || 0);
  const size = Number(params.size || 10);
  const start = page * size;
  const slice = items.slice(start, start + size);
  return {
    content: slice,
    number: page,
    size,
    totalElements: items.length,
    totalPages: Math.max(1, Math.ceil(items.length / size)),
    first: page === 0,
    last: start + size >= items.length,
  };
};

const applySort = (items, params) => {
  const sortParam = params.sort;
  if (!sortParam) return items;
  const sorts = Array.isArray(sortParam) ? sortParam : [sortParam];
  return [...items].sort((a, b) => {
    for (const s of sorts) {
      const [field, direction = 'asc'] = s.split(',');
      const va = a[field];
      const vb = b[field];
      if (va === vb) continue;
      const cmp = va > vb ? 1 : -1;
      return direction === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
};

export default [
  // GET /customer-ms/api/v1/clients?query=&status=&type=&sort=&page=&size=
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/clients$/,
    handler: ({ params }) => {
      const filtered = filterClients(params);
      const sorted = applySort(filtered, params);
      return paginate(sorted, params);
    },
  },
  // POST /customer-ms/api/v1/clients   → create
  {
    method: 'POST',
    pattern: /^\/customer-ms\/api\/v1\/clients$/,
    handler: ({ body }) => {
      const id = `cli-${String(ALL_CLIENTS.length + 1).padStart(4, '0')}`;
      const created = { id, ...body, createDate: new Date().toISOString(), nbrOpenAccount: 0, nbrOpenAlert: 0 };
      ALL_CLIENTS.unshift(created);
      return created;
    },
  },
  // PUT /customer-ms/api/v1/clients    → update
  {
    method: 'PUT',
    pattern: /^\/customer-ms\/api\/v1\/clients$/,
    handler: ({ body }) => {
      const idx = ALL_CLIENTS.findIndex((c) => c.id === body.id);
      if (idx >= 0) ALL_CLIENTS[idx] = { ...ALL_CLIENTS[idx], ...body };
      return ALL_CLIENTS[idx] || body;
    },
  },
  // GET /customer-ms/api/v1/clients/stat-status-type?query=
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/clients\/stat-status-type$/,
    handler: ({ params }) => computeTypeStats(filterClients(params)),
  },
  // GET /customer-ms/api/v1/clients/:id
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/clients\/(cli-\d+)$/,
    handler: ({ match }) => findClient(match[1]) || {},
  },
  // GET /customer-ms/api/v1/clients/:id/note
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/clients\/(cli-\d+)\/note$/,
    handler: ({ match }) => buildNotesForClient(match[1]),
  },
  // PUT /customer-ms/api/v1/clients/:id/note
  {
    method: 'PUT',
    pattern: /^\/customer-ms\/api\/v1\/clients\/(cli-\d+)\/note$/,
    handler: ({ match, body }) => ({ ...buildNotesForClient(match[1]), note: body }),
  },
  // GET /customer-ms/api/v1/:clientId/accounts/search
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/(cli-\d+)\/accounts\/search$/,
    handler: ({ match, params }) => {
      const all = buildAccountsForClient(match[1]);
      const q = (params.query || '').toLowerCase();
      const filtered = all.filter((u) => {
        if (q && !`${u.firstName} ${u.lastName} ${u.userId}`.toLowerCase().includes(q)) return false;
        if (params.accountStatus && u.accountStatus !== params.accountStatus) return false;
        if (params.conventionStatus && u.conventionStatus !== params.conventionStatus) return false;
        if (params.level && u.level !== params.level) return false;
        if (params.type && u.type !== params.type) return false;
        return true;
      });
      return paginate(applySort(filtered, params), params);
    },
  },
  // POST /customer-ms/api/v1/:clientId/accounts/stat
  {
    method: 'POST',
    pattern: /^\/customer-ms\/api\/v1\/(cli-\d+)\/accounts\/stat$/,
    handler: ({ match }) => buildClientUsersCount(match[1]),
  },
  // GET /customer-ms/api/v1/convention-families/clients/:id
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/convention-families\/clients\/(cli-\d+)$/,
    handler: ({ match, params }) => {
      let all = buildConventionsForClient(match[1]);
      if (params.securityLevel) all = all.filter((f) => f.securityLevel === params.securityLevel);
      if (params.active !== '' && params.active !== undefined) {
        const wanted = params.active === 'true';
        all = all.filter((f) => f.active === wanted);
      }
      return all;
    },
  },
  // PUT /customer-ms/api/v1/convention-families/clients/:id
  {
    method: 'PUT',
    pattern: /^\/customer-ms\/api\/v1\/convention-families\/clients\/(cli-\d+)$/,
    handler: ({ match, body }) => ({ updated: true, clientId: match[1], payload: body }),
  },
  // GET /customer-ms/api/v1/:clientId/statistics/opened-account
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/(cli-\d+)\/statistics\/opened-account$/,
    handler: ({ match }) => buildOpenAccountsSeries(match[1]),
  },
  // GET /customer-ms/api/v1/:clientId/statistics/kpi
  {
    method: 'GET',
    pattern: /^\/customer-ms\/api\/v1\/(cli-\d+)\/statistics\/kpi$/,
    handler: ({ match, params }) => buildKpiSeries(match[1], params.kpiType),
  },
];
